// src/pages/Home.js
import React, { useEffect } from "react";
import SearchBar from "../components/SearchBar";
import PhotoGrid from "../components/PhotoGrid";
import AdSlot    from "../components/AdSlot";
import { usePexels } from "../hooks/usePexels";

const SOURCES = [
  { key: "all",       label: "✦ All",           desc: "Pexels + Wallhaven" },
  { key: "pexels",    label: "📸 Stock Photos",  desc: "Pexels"             },
  { key: "wallhaven", label: "🖼 HD Wallpapers", desc: "Wallhaven"          },
];

const ORIENTATIONS = [
  { key: "",          label: "⊞ All Sizes"   },
  { key: "landscape", label: "⬛ Horizontal" },
  { key: "portrait",  label: "▮ Vertical"   },
  { key: "squarish",  label: "■ Square"     },
];

const STATS = [
  { number: "10M+",   label: "Free Photos"     },
  { number: "4K",     label: "Ultra HD Quality" },
  { number: "100%",   label: "Free Forever"     },
  { number: "0",      label: "Attribution Needed" },
];

const Home = () => {
  const {
    photos, loading, error, retrying, totalResults,
    query, hasMore, source, orientation,
    loadCurated, search, loadMore,
    switchSource, switchOrientation, retry,
  } = usePexels();

  useEffect(() => {
    loadCurated("all", ""); // eslint-disable-line
  }, []);

  return (
    <div className="page">
      {/* ── Hero ── */}
      <header className="hero">
        <div className="hero__bg" aria-hidden="true">
          <div className="hero__blob hero__blob--1" />
          <div className="hero__blob hero__blob--2" />
          <div className="hero__blob hero__blob--3" />
        </div>
        <div className="hero__content">
          <div className="hero__badge">
            🌟 #1 Free HD Wallpaper & Stock Photo Site
          </div>
          <h1 className="hero__title">
            Download Free<br />
            <span className="hero__accent">HD Wallpapers &amp; Photos</span>
          </h1>
          <p className="hero__sub">
            10 million+ stunning 4K wallpapers and stock photos — completely free.
            No signup, no watermark, no copyright. Download instantly!
          </p>
          <SearchBar onSearch={search} currentQuery={query} />

          {/* Stats row */}
          <div className="hero-stats">
            {STATS.map(s => (
              <div key={s.label} className="hero-stat">
                <span className="hero-stat__number">{s.number}</span>
                <span className="hero-stat__label">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </header>

      {/* AD SLOT */}
      <AdSlot type="banner" />

      <div className="container">
        {/* Source tabs */}
        <div className="source-tabs">
          {SOURCES.map(s => (
            <button
              key={s.key}
              className={`source-tab${source === s.key ? " source-tab--active" : ""}`}
              onClick={() => switchSource(s.key)}
            >
              <span className="source-tab__label">{s.label}</span>
              <span className="source-tab__desc">{s.desc}</span>
            </button>
          ))}
        </div>

        {/* Orientation filter */}
        <div className="orient-filters">
          {ORIENTATIONS.map(o => (
            <button
              key={o.key}
              className={`orient-btn${orientation === o.key ? " orient-btn--active" : ""}`}
              onClick={() => switchOrientation(o.key)}
            >
              {o.label}
            </button>
          ))}
          <button
            className="orient-btn orient-btn--refresh"
            onClick={() => loadCurated(source, orientation)}
            title="Load new photos"
          >
            🔄 Refresh
          </button>
        </div>

        {/* Results header */}
        <div className="results-header">
          {query ? (
            <>
              <h2 className="results-header__title">
                Results for <span className="results-header__query">"{query}"</span>
              </h2>
              {totalResults > 0 && (
                <span className="results-header__count">
                  {totalResults.toLocaleString()} photos
                </span>
              )}
            </>
          ) : (
            <h2 className="results-header__title">
              {source === "wallhaven" ? "🖼 Top HD Wallpapers"
             : source === "pexels"   ? "📸 Curated Stock Photos"
             :                         "✨ Trending Photos & Wallpapers"}
            </h2>
          )}
        </div>

        <PhotoGrid
          photos={photos}
          loading={loading}
          error={error}
          hasMore={hasMore}
          onLoadMore={loadMore}
          onRetry={retry}
          retrying={retrying}
        />

        {/* SEO text section — helps Google rank for keywords */}
        {!query && !loading && (
          <div className="seo-section">
            <h2 className="seo-section__title">
              Free HD Wallpapers & Stock Photos — Download Now
            </h2>
            <div className="seo-section__grid">
              <div className="seo-card">
                <span className="seo-card__icon">🖼</span>
                <h3>4K HD Wallpapers</h3>
                <p>Download ultra high resolution wallpapers for desktop, mobile and tablet. Nature, abstract, cityscape, anime and more.</p>
              </div>
              <div className="seo-card">
                <span className="seo-card__icon">📸</span>
                <h3>Free Stock Photos</h3>
                <p>Millions of professional stock photos for blogs, websites and social media. No attribution required. Commercial use allowed.</p>
              </div>
              <div className="seo-card">
                <span className="seo-card__icon">🤖</span>
                <h3>AI Image Generator</h3>
                <p>Create unique images with AI. Type any description and get a stunning image in seconds. Completely free, unlimited generations.</p>
              </div>
              <div className="seo-card">
                <span className="seo-card__icon">⚡</span>
                <h3>Instant Download</h3>
                <p>Download in multiple sizes — Original, Large, Medium, Small. No watermarks, no registration needed for basic downloads.</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Home;
